// Sentry must init before any other meaningful module work.
import { initSentry } from "@engagexp/observability";
initSentry({
  product: "gamin_engine",
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.PROD ? "production" : "development",
  release: import.meta.env.VITE_APP_VERSION,
});

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import {
  AnalyticsProvider,
  attachRouterPageViewTracking,
} from "@engagexp/analytics";
import { ClerkIdentifyBridge } from "@engagexp/analytics/clerk";
import { routeTree } from "./routeTree.gen";
import "./styles/index.css";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  "https://us.i.posthog.com";

const router = createRouter({ routeTree, defaultPreload: "intent" });
attachRouterPageViewTracking(router);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function MissingClerkKeyNotice() {
  return (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
      <div className="max-w-[560px] rounded-2xl border border-border bg-card p-8 shadow-2xl">
        <p className="ge-font-mono text-primary mb-3 tracking-[0.18em] uppercase text-xs font-semibold">
          Clerk Setup Required
        </p>
        <h1 className="ge-font-display text-foreground mb-3 text-[28px] tracking-[0.02em]">
          Add your Clerk publishable key
        </h1>
        <p className="text-muted-foreground leading-relaxed mb-6">
          Provide <code>VITE_CLERK_PUBLISHABLE_KEY</code> via Doppler (or your
          local env injection), then restart the Vite dev server.
        </p>
        <pre className="rounded-xl bg-background p-4 text-sm text-foreground overflow-x-auto">
          <code>VITE_CLERK_PUBLISHABLE_KEY=pk_live_… or pk_test_…</code>
        </pre>
      </div>
    </main>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing root element");

const clerkKeyConfigured = Boolean(
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

createRoot(root).render(
  <StrictMode>
    {clerkKeyConfigured ? (
      <ClerkProvider
        publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
        afterSignOutUrl="/"
      >
        <AnalyticsProvider
          product="gamin_engine"
          apiKey={POSTHOG_KEY ?? ""}
          apiHost={POSTHOG_HOST}
          environment={import.meta.env.PROD ? "production" : "development"}
          platform="web"
          disabled={!POSTHOG_KEY}
        >
          <ClerkIdentifyBridge />
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </AnalyticsProvider>
      </ClerkProvider>
    ) : (
      <MissingClerkKeyNotice />
    )}
  </StrictMode>,
);
